import { useState, useCallback } from 'react';

import { BulkOperation } from '../apis/types';

interface UseBulkOperationHeaderEditProps {
  title?: string;
  description?: string;
  onUpdate: (data: {
    title?: string;
    description?: string;
  }) => Promise<BulkOperation>;
}

interface UseBulkOperationHeaderEditReturn {
  isEditing: boolean;
  editedTitle: string;
  editedDescription: string;
  setEditedTitle: (title: string) => void;
  setEditedDescription: (description: string) => void;
  handleEdit: () => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
}

/**
 * Custom hook to manage bulk operation header edit state and operations
 */
export const useBulkOperationHeaderEdit = ({
  title,
  description,
  onUpdate,
}: UseBulkOperationHeaderEditProps): UseBulkOperationHeaderEditReturn => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title || '');
  const [editedDescription, setEditedDescription] = useState(description || '');

  const handleEdit = useCallback(() => {
    setEditedTitle(title || '');
    setEditedDescription(description || '');
    setIsEditing(true);
  }, [title, description]);

  const handleSave = useCallback(async () => {
    try {
      await onUpdate({
        title: editedTitle.trim() || undefined,
        description: editedDescription.trim() || undefined,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update bulk operation:', error);
    }
  }, [editedTitle, editedDescription, onUpdate]);

  const handleCancel = useCallback(() => {
    setEditedTitle(title || '');
    setEditedDescription(description || '');
    setIsEditing(false);
  }, [title, description]);

  return {
    isEditing,
    editedTitle,
    editedDescription,
    setEditedTitle,
    setEditedDescription,
    handleEdit,
    handleSave,
    handleCancel,
  };
};
